// Shaping + diffing helpers for profile-run results payloads, shared by the data source
// detail page (history timeline, prev-size column) and the per-table detail page (column
// diff). `getResults` returns the worker payload wrapped as `{ payload }` on some paths
// and bare on others — callers pass whichever; normalizeSnapshot unwraps both.

// Distil a run's results payload to the fields the timeline + table/column diff +
// historical snapshot view need. Columns keep name + dataType (dataType is null for
// legacy snapshots and file sources). Per-table columnCount/rowCount let the data source
// detail page render a past snapshot's tables list when the user clicks a history bar;
// the results payload does NOT carry index/PK/FK/protection stats (latest-summary only).
export function normalizeSnapshot(jobId, res) {
  const payload = res && res.payload ? res.payload : res;
  if (!payload) return null;
  const tables = Array.isArray(payload.tables) ? payload.tables : [];
  return {
    jobId,
    snapshotTakenAt: payload.snapshotTakenAt || null,
    totalSizeBytes: payload.totalSizeBytes != null ? Number(payload.totalSizeBytes) : 0,
    tableCount: payload.tableCount != null ? payload.tableCount : tables.length,
    userCount: payload.userCount != null ? payload.userCount : null,
    users: Array.isArray(payload.users)
      ? payload.users.map((u) => ({
          username: u.username,
          roles: Array.isArray(u.roles) ? u.roles : [],
          lastLogin: u.lastLogin || null,
        }))
      : [],
    tables: tables.map((t) => ({
      tableName: t.tableName,
      // Owning schema (C2D-1116/1093) — null for legacy snapshots and file sources.
      schemaName: t.schemaName || null,
      sizeBytes: t.sizeBytes != null ? Number(t.sizeBytes) : null,
      columnCount: t.columnCount != null ? t.columnCount : null,
      rowCount: t.rowCount != null ? Number(t.rowCount) : null,
      notice: t.notice || null,
      columns: Array.isArray(t.columns)
        ? t.columns
            .filter((c) => c && c.name)
            .map((c) => ({
              name: c.name,
              dataType: c.dataType != null ? c.dataType : null,
              // Per-column population stats (V23) — null for legacy snapshots / skipped tables.
              // statsProvenance: EXACT (whole table counted), CATALOG (engine statistics),
              // SAMPLE (bounded first-N read; distinct is a floor, null rate biased).
              nullCount: c.nullCount != null ? Number(c.nullCount) : null,
              distinctCount: c.distinctCount != null ? Number(c.distinctCount) : null,
              sampledRows: c.sampledRows != null ? Number(c.sampledRows) : null,
              statsProvenance: c.statsProvenance || null,
              // PK / UNIQUE membership (V32) — [{name, kind, columnCount, position, partial,
              // enforced}]; null for legacy snapshots and unconstrained columns.
              keyConstraints: Array.isArray(c.keyConstraints) && c.keyConstraints.length > 0
                ? c.keyConstraints.map((k) => ({
                    name: k.name || '',
                    kind: k.kind === 'PRIMARY_KEY' ? 'PRIMARY_KEY' : 'UNIQUE',
                    columnCount: Number(k.columnCount) || 1,
                    position: Number(k.position) || 1,
                    partial: Boolean(k.partial),
                    enforced: k.enforced == null ? null : Boolean(k.enforced),
                  }))
                : null,
            }))
        : [],
    })),
  };
}

// The column list ([{name, dataType}]) for one table in a snapshot, or null if the
// snapshot has no such table (or no snapshot at all).
export function columnsForTable(snapshot, tableName) {
  if (!snapshot || !Array.isArray(snapshot.tables)) return null;
  const t = snapshot.tables.find((tbl) => tbl.tableName === tableName);
  return t ? t.columns : null;
}

// Diff a table's current columns against the previous snapshot's, preserving current
// (ordinal) order and appending removed columns at the end. Each row:
//   { name, dataType, prevType, status: 'added' | 'removed' | 'changed' | 'same' }
// `prevType` is only meaningful for 'changed' (the old type); it's null otherwise.
export function diffColumns(current, previous) {
  const cur = Array.isArray(current) ? current : [];
  const hadPrev = Array.isArray(previous);
  const prev = hadPrev ? previous : [];
  const prevByName = new Map(prev.map((c) => [c.name, c]));
  const curNames = new Set(cur.map((c) => c.name));

  const rows = cur.map((c) => {
    const p = prevByName.get(c.name);
    // Population stats ride along verbatim (current snapshot's values; not diffed).
    const stats = {
      nullCount: c.nullCount != null ? c.nullCount : null,
      distinctCount: c.distinctCount != null ? c.distinctCount : null,
      sampledRows: c.sampledRows != null ? c.sampledRows : null,
      statsProvenance: c.statsProvenance || null,
      keyConstraints: Array.isArray(c.keyConstraints) ? c.keyConstraints : null,
    };
    if (!hadPrev || !p) {
      // No prior snapshot at all → everything is just "current" (status 'same', no
      // add/remove coloring). A prior snapshot that lacks this column → genuinely added.
      return { name: c.name, dataType: c.dataType, prevType: null, status: hadPrev ? 'added' : 'same', ...stats };
    }
    const changed = (p.dataType || null) !== (c.dataType || null);
    return { name: c.name, dataType: c.dataType, prevType: changed ? p.dataType : null, status: changed ? 'changed' : 'same', ...stats };
  });

  const removed = hadPrev
    ? prev
        .filter((p) => !curNames.has(p.name))
        .map((p) => ({ name: p.name, dataType: p.dataType, prevType: null, status: 'removed' }))
    : [];

  return {
    rows: rows.concat(removed),
    hadPrev,
    addedCount: rows.filter((r) => r.status === 'added').length,
    removedCount: removed.length,
    changedCount: rows.filter((r) => r.status === 'changed').length,
  };
}
